
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import * as echarts from 'echarts';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import gdbx from '@/store/modules/gdbx';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TreeMapFilterable,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class AgentSalesAnalysisTreeMap extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers', 'sales'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange', 'agents'];

	public result: any = [];
	public series: echarts.ECharts[] | any = [];

	public selectedGrouping: any = null;

	public get groupingList(): Array<ValueText<string>> {
		const list = [
			{ text: 'Stock Item', value: 'stockItems' },
			{ text: 'Stock Group', value: 'stockGroup' },
			{ text: 'Customer', value: 'customer' },
			{ text: 'Company Categories', value: 'customerCategory' },
			{ text: 'Area', value: 'area' },
			{ text: 'Current vs New ', value: 'currentNew' },
			{ text: 'New Customer', value: 'newCustomer' },
			{ text: 'Current Customer', value: 'currentCustomer' },
		];

		return list;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAgents,
			selectedGrouping,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedGrouping,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	// Cached raw data
	private _cachedData: {
		slIvCsRows: any[];
		slDnRows: any[];
		slCnRows: any[];
		arIvRows: any[];
		arDnRows: any[];
		arCnRows: any[];
		globalRows: any[];
		slIvCsSummary: { total: number };
		slDnSummary: { total: number };
		arIvSummary: { total: number };
		arDnSummary: { total: number };
		slCnTotal: number;
		arCnTotal: number;
	} | null = null;
	private _cachedDataKey: string = '';

	private get _dataFetchKey(): string {
		return JSON.stringify([
			this.selectedDateRange,
			this.selectedAgents,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		// === Data Fetch Phase (cached) ===
		const currentFetchKey = this._dataFetchKey;
		const useCache = this._cachedData !== null
			&& this._cachedDataKey === currentFetchKey;

		let slIvCsRows: any[];
		let slDnRows: any[];
		let slCnRows: any[];
		let arIvRows: any[];
		let arDnRows: any[];
		let arCnRows: any[];
		let globalRows: any[];
		let slIvCsSummary: { total: number };
		let slDnSummary: { total: number };
		let arIvSummary: { total: number };
		let arDnSummary: { total: number };
		let slCnTotal: number;
		let arCnTotal: number;

		if (useCache) {
			slIvCsRows = this._cachedData!.slIvCsRows;
			slDnRows = this._cachedData!.slDnRows;
			slCnRows = this._cachedData!.slCnRows;
			arIvRows = this._cachedData!.arIvRows;
			arDnRows = this._cachedData!.arDnRows;
			arCnRows = this._cachedData!.arCnRows;
			globalRows = this._cachedData!.globalRows;
			slIvCsSummary = this._cachedData!.slIvCsSummary;
			slDnSummary = this._cachedData!.slDnSummary;
			arIvSummary = this._cachedData!.arIvSummary;
			arDnSummary = this._cachedData!.arDnSummary;
			slCnTotal = this._cachedData!.slCnTotal;
			arCnTotal = this._cachedData!.arCnTotal;
		} else {
			const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
			const dateStart = this.selectedDateRange[0];
			const dateEnd = this.selectedDateRange[1];
			let agentFilter = '';
			if (this.selectedAgents.length > 0) {
				const list = this.selectedAgents
					.map((a) => `'${escapeStr(a)}'`).join(',');
				agentFilter = ` AND "AGENT" IN (${list})`;
			}

			const salesTable = getTableName('sales');
			const cnTable = getTableName('creditNotes');
			const arTableName = getTableName('customerTrades');
			const globalTable = getTableName('globalCustomers');
			const dateFilter = ` AND TRY_CAST("DOCDATE" AS BIGINT) >= ${dateStart}`
				+ ` AND TRY_CAST("DOCDATE" AS BIGINT) <= ${dateEnd}`;

			const queryHead = await this._ensureTablesLoaded([
				'sales', 'creditNotes', 'customerTrades', 'globalCustomers',
			] as any[]);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.3, 0.9);

			const slGroupBy = `GROUP BY "CODE", "AGENT", "COMPANYCATEGORY", "STOCKGROUP", "ITEMCODE"`;
			const slSelect = `"CODE" as code, "AGENT" as agent,
				"COMPANYCATEGORY" as companyCategory,
				"STOCKGROUP" as stockGroup, "ITEMCODE" as itemCode,
				SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as amount`;
			const arGroupBy = `GROUP BY "CODE", "AGENT", "COMPANYCATEGORY"`;
			const arSelect = `"CODE" as code, "AGENT" as agent,
				"COMPANYCATEGORY" as companyCategory,
				SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as amount`;

			const [
				slIvCsResult, slDnResult, slCnResult,
				arIvResult, arDnResult, arCnResult,
				globalResult,
				slIvCsSum, slDnSum, arIvSum, arDnSum,
				slCnTotalResult, arCnTotalResult,
			] = await Promise.all([
				queryHead.rawQuery(`SELECT ${slSelect} FROM ${salesTable}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" IN ('IV', 'CS') ${slGroupBy}`),
				queryHead.rawQuery(`SELECT ${slSelect} FROM ${salesTable}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'DN' ${slGroupBy}`),
				queryHead.rawQuery(`SELECT ${slSelect} FROM ${cnTable}
					WHERE 1=1${dateFilter}${agentFilter} ${slGroupBy}`),
				queryHead.rawQuery(`SELECT ${arSelect} FROM ${arTableName}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'IV' ${arGroupBy}`),
				queryHead.rawQuery(`SELECT ${arSelect} FROM ${arTableName}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'DN' ${arGroupBy}`),
				queryHead.rawQuery(`SELECT ${arSelect} FROM ${arTableName}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'CN' ${arGroupBy}`),
				queryHead.rawQuery(`SELECT "CODE" as code,
					"COMPANYCATEGORY" as companyCategory,
					"COMPANYCATEGORYDESC" as companyCategoryDesc,
					"AGENT" as agent,
					TRY_CAST("FIRSTDEAL" AS BIGINT) as firstDeal,
					"FIRSTDEALAGENT" as firstDealAgent,
					"AREADESC" as areaDesc
					FROM ${globalTable} WHERE 1=1`),
				queryHead.rawQuery(`SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
					FROM ${salesTable}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" IN ('IV', 'CS')`),
				queryHead.rawQuery(`SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
					FROM ${salesTable}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'DN'`),
				queryHead.rawQuery(`SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
					FROM ${arTableName}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'IV'`),
				queryHead.rawQuery(`SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
					FROM ${arTableName}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'DN'`),
				queryHead.rawQuery(`SELECT COALESCE(SUM(TRY_CAST("AMOUNT" AS DOUBLE)), 0) as total
					FROM ${cnTable}
					WHERE 1=1${dateFilter}${agentFilter}`),
				queryHead.rawQuery(`SELECT COALESCE(SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)), 0) as total
					FROM ${arTableName}
					WHERE 1=1${dateFilter}${agentFilter} AND "DOCTYPE" = 'CN'`),
			]);
			this.checkInterrupted(jobId);

			const parseSlRow = (r: any) => ({
				code: r.code as string, agent: r.agent as string,
				companyCategory: (r.companyCategory || '') as string,
				stockGroup: (r.stockGroup || '') as string,
				itemCode: (r.itemCode || '') as string,
				amount: Number(r.amount) || 0,
			});
			const parseArRow = (r: any) => ({
				code: r.code as string, agent: r.agent as string,
				companyCategory: (r.companyCategory || '') as string,
				amount: Number(r.amount) || 0,
			});

			slIvCsRows = slIvCsResult.map(parseSlRow);
			slDnRows = slDnResult.map(parseSlRow);
			slCnRows = slCnResult.map(parseSlRow);
			arIvRows = arIvResult.map(parseArRow);
			arDnRows = arDnResult.map(parseArRow);
			arCnRows = arCnResult.map(parseArRow);
			globalRows = globalResult.map((r: any) => ({
				code: r.code as string,
				companyCategory: (r.companyCategory || '') as string,
				companyCategoryDesc: (r.companyCategoryDesc || '') as string,
				agent: (r.agent || '') as string,
				firstDeal: Number(r.firstDeal) || 0,
				firstDealAgent: (r.firstDealAgent || '') as string,
				areaDesc: (r.areaDesc || '') as string,
			}));
			slIvCsSummary = { total: Number(slIvCsSum[0] && slIvCsSum[0].total) || 0 };
			slDnSummary = { total: Number(slDnSum[0] && slDnSum[0].total) || 0 };
			arIvSummary = { total: Number(arIvSum[0] && arIvSum[0].total) || 0 };
			arDnSummary = { total: Number(arDnSum[0] && arDnSum[0].total) || 0 };
			slCnTotal = Number(slCnTotalResult[0] && slCnTotalResult[0].total) || 0;
			arCnTotal = Number(arCnTotalResult[0] && arCnTotalResult[0].total) || 0;

			this._cachedData = {
				slIvCsRows, slDnRows, slCnRows,
				arIvRows, arDnRows, arCnRows, globalRows,
				slIvCsSummary, slDnSummary, arIvSummary, arDnSummary,
				slCnTotal, arCnTotal,
			};
			this._cachedDataKey = currentFetchKey;
		}

		// === Processing Phase (always runs) ===
		const includeDN = this.includeDebitNote;
		const includeCN = this.includeCreditNote;
		const slMode = this.slPhModuleOnly;

		const slIvdnRows = includeDN ? [...slIvCsRows, ...slDnRows] : slIvCsRows;
		const slCnEff = includeCN ? slCnRows : [];
		const arIvdnRows = includeDN ? [...arIvRows, ...arDnRows] : arIvRows;
		const arCnEff = includeCN ? arCnRows : [];

		const mainIvdnRows = slMode ? slIvdnRows : arIvdnRows;
		const mainCnRows = slMode ? slCnEff : arCnEff;

		// Derive customer list and global customer lookup
		const customerSet = new Set<string>();
		for (const row of mainIvdnRows) { customerSet.add(row.code); }

		const globalByCode = new Map<string, any>();
		for (const row of globalRows) {
			if (customerSet.has(row.code)) { globalByCode.set(row.code, row); }
		}

		// New vs existing customers
		const newCustomerSet = new Set<string>();
		for (const code of customerSet) {
			const g = globalByCode.get(code);
			if (g) {
				const matchAgent = this.selectedAgents.length === 0
					|| this.selectedAgents.indexOf(g.firstDealAgent) >= 0;
				const matchDate = g.firstDeal >= this.selectedDateRange[0]
					&& g.firstDeal <= this.selectedDateRange[1];
				if (matchAgent && matchDate) { newCustomerSet.add(code); }
			}
		}

		// Customer area lookup
		const customerAreaList: { [area: string]: string[] } = {};
		for (const [code, g] of globalByCode) {
			const area = g.areaDesc || '';
			if (!customerAreaList[area]) { customerAreaList[area] = []; }
			customerAreaList[area].push(code);
		}

		// === Helper functions to build dimension data ===
		const buildCustomerDD = (rows: any[]): any[] => {
			const map = new Map<string, number>();
			for (const row of rows) {
				map.set(row.code, (map.get(row.code) || 0) + Number(row.amount));
			}
			return Array.from(map.entries()).map(([code, sum]) => ({
				text: gdbx.customerNames[code] || code,
				value: [code], sum,
			}));
		};
		const buildAgentCustomerDD = (rows: any[]): any[] => {
			const agentMap = new Map<string, Map<string, number>>();
			for (const row of rows) {
				if (!agentMap.has(row.agent)) {
					agentMap.set(row.agent, new Map());
				}
				const cm = agentMap.get(row.agent)!;
				cm.set(row.code, (cm.get(row.code) || 0) + Number(row.amount));
			}
			return Array.from(agentMap.entries()).map(([agent, cm]) => {
				const sub = Array.from(cm.entries()).map(([code, sum]) => ({
					text: gdbx.customerNames[code] || code,
					value: [code], sum,
				}));
				return {
					text: gdbx.agentNames[agent] || agent,
					value: [agent],
					sum: sub.reduce((a, b) => a + b.sum, 0),
					subDimension: sub,
				};
			});
		};
		const buildCategoryCustomerDD = (rows: any[]): any[] => {
			const catMap = new Map<string, Map<string, number>>();
			for (const row of rows) {
				const cat = row.companyCategory || '';
				if (!catMap.has(cat)) { catMap.set(cat, new Map()); }
				const cm = catMap.get(cat)!;
				cm.set(row.code, (cm.get(row.code) || 0) + Number(row.amount));
			}
			return Array.from(catMap.entries()).map(([cat, cm]) => {
				const sub = Array.from(cm.entries()).map(([code, sum]) => ({
					text: gdbx.customerNames[code] || code,
					value: [code], sum,
				}));
				return {
					text: gdbx.customerCategoryNames[cat] || cat,
					value: [cat],
					sum: sub.reduce((a, b) => a + b.sum, 0),
					subDimension: sub,
				};
			});
		};
		const buildStockItemDD = (rows: any[]): any[] => {
			const map = new Map<string, number>();
			for (const row of rows) {
				map.set(
					row.itemCode,
					(map.get(row.itemCode) || 0) + Number(row.amount),
				);
			}
			return Array.from(map.entries()).map(([itemCode, sum]) => ({
				text: gdbx.stockItemNames[itemCode] || itemCode,
				value: [itemCode], sum,
			}));
		};
		const buildStockGroupItemDD = (rows: any[]): any[] => {
			const grpMap = new Map<string, Map<string, number>>();
			for (const row of rows) {
				const grp = row.stockGroup || '';
				if (!grpMap.has(grp)) { grpMap.set(grp, new Map()); }
				const im = grpMap.get(grp)!;
				im.set(
					row.itemCode,
					(im.get(row.itemCode) || 0) + Number(row.amount),
				);
			}
			return Array.from(grpMap.entries()).map(([grp, im]) => {
				const sub = Array.from(im.entries()).map(([itemCode, sum]) => ({
					text: gdbx.stockItemNames[itemCode] || itemCode,
					value: [itemCode], sum,
				}));
				return {
					text: gdbx.stockGroupNames[grp] || grp,
					value: [grp],
					sum: sub.reduce((a, b) => a + b.sum, 0),
					subDimension: sub,
				};
			});
		};

		// Build DimensionData for all needed groupings
		const allCustomerIvdnDD = buildCustomerDD(mainIvdnRows);
		const allCustomerCnDD = includeCN ? buildCustomerDD(mainCnRows) : [];

		const newCustomerIvdnDD = allCustomerIvdnDD
			.filter((dd) => newCustomerSet.has(dd.value[0] as string));
		const newCustomerCNDD = allCustomerCnDD
			.filter((dd) => newCustomerSet.has(dd.value[0] as string));
		const existingCustomerIvdnDD = allCustomerIvdnDD
			.filter((dd) => !newCustomerSet.has(dd.value[0] as string));
		const existingCustomerCNDD = allCustomerCnDD
			.filter((dd) => !newCustomerSet.has(dd.value[0] as string));

		// AR Document amount for stock groupings in AR mode
		let arDocumentAmount = 0;
		const isStockGrouping = this.selectedGrouping === 'stockItems'
			|| this.selectedGrouping === 'stockGroup'
			|| !this.selectedGrouping;
		if (!slMode && isStockGrouping) {
			const arTotal = arIvSummary.total
				+ (includeDN ? arDnSummary.total : 0)
				- (includeCN ? arCnTotal : 0);
			const slTotal = slIvCsSummary.total
				+ (includeDN ? slDnSummary.total : 0)
				- (includeCN ? slCnTotal : 0);
			arDocumentAmount = arTotal - slTotal;
		}

		// Build tradeDD and tradeCNDD based on grouping
		let tradeDD: any[] = [];
		let tradeCNDD: any[] = [];

		switch (this.selectedGrouping) {
			case 'stockGroup':
				tradeDD = buildStockGroupItemDD(slIvdnRows);
				tradeCNDD = includeCN ? buildStockGroupItemDD(slCnEff) : [];
				break;
			case 'customerCategory':
				tradeDD = buildCategoryCustomerDD(mainIvdnRows);
				tradeCNDD = includeCN
					? buildCategoryCustomerDD(mainCnRows) : [];
				break;
			case 'salesAgent':
				tradeDD = buildAgentCustomerDD(mainIvdnRows);
				tradeCNDD = includeCN
					? buildAgentCustomerDD(mainCnRows) : [];
				break;
			case 'newCustomer':
				tradeDD = newCustomerIvdnDD;
				tradeCNDD = newCustomerCNDD;
				break;
			case 'currentCustomer':
				tradeDD = existingCustomerIvdnDD;
				tradeCNDD = existingCustomerCNDD;
				break;
			case 'area':
			case 'customer':
				tradeDD = allCustomerIvdnDD;
				tradeCNDD = allCustomerCnDD;
				break;
			default:
				tradeDD = buildStockItemDD(slIvdnRows);
				tradeCNDD = includeCN ? buildStockItemDD(slCnEff) : [];
		}

		this.generateLoadingText(1);
		this.result = [];

		// Build CN lookup maps for code-based matching
		const cnLookup = new Map<string, number>();
		for (const dd of tradeCNDD) {
			cnLookup.set(dd.value[0] as string, dd.sum);
		}
		const getCnForCode = (code: string) =>
			includeCN ? (cnLookup.get(code) || 0) : 0;

		const newCnLookup = new Map<string, number>();
		for (const dd of newCustomerCNDD) {
			newCnLookup.set(dd.value[0] as string, dd.sum);
		}
		const existCnLookup = new Map<string, number>();
		for (const dd of existingCustomerCNDD) {
			existCnLookup.set(dd.value[0] as string, dd.sum);
		}

		const treeColor = [
			'#8975ea', '#acbeff', '#accdff',
			'#acdaff', '#cdc8f9', '#c7b5e2',
		];

		if (this.selectedGrouping === 'currentNew') {
			const newCNTotal = includeCN
				? newCustomerCNDD.map((item) => item.sum)
					.reduce((a, b) => a + b, 0)
				: 0;
			const existCNTotal = includeCN
				? existingCustomerCNDD.map((item) => item.sum)
					.reduce((a, b) => a + b, 0)
				: 0;

			this.result.push({
				name: 'New Customer',
				value: newCustomerIvdnDD
					.map((item) => item.sum)
					.reduce((a, b) => a + b, 0) - newCNTotal,
				color: treeColor,
				colorSaturation: [0.9, 0.4],
				children: newCustomerIvdnDD.map((customer) => {
					const cnSum = includeCN
						? (newCnLookup.get(customer.value[0] as string) || 0)
						: 0;
					return {
						name: customer.text,
						value: customer.sum - cnSum,
						color: treeColor,
						colorSaturation: [0.9, 0.4],
					};
				}),
			});
			this.result.push({
				name: 'Existing Customer',
				value: existingCustomerIvdnDD
					.map((item) => item.sum)
					.reduce((a, b) => a + b, 0) - existCNTotal,
				color: treeColor,
				colorSaturation: [0.9, 0.4],
				children: existingCustomerIvdnDD.map((customer) => {
					const cnSum = includeCN
						? (existCnLookup.get(customer.value[0] as string) || 0)
						: 0;
					return {
						name: customer.text,
						value: customer.sum - cnSum,
						color: treeColor,
						colorSaturation: [0.9, 0.4],
					};
				}),
			});
		} else if (this.selectedGrouping === 'area') {
			const ivdnByCode = new Map<string, any>();
			for (const dd of tradeDD) {
				ivdnByCode.set(dd.value[0] as string, dd);
			}

			const areaList = Object.keys(customerAreaList);
			const codeList: string[][] = Object.values(customerAreaList);

			areaList.forEach((area, index) => {
				const codes: string[] = codeList[index];
				const object = {
					name: area,
					value: 0,
					color: treeColor,
					colorSaturation: [0.9, 0.4],
					children: [] as Array<{
						name: string;
						value: number;
						color: string[];
					}>,
				};
				codes.forEach((code) => {
					const ivdn = ivdnByCode.get(code);
					if (ivdn) {
						const cnSum = getCnForCode(code);
						object.children.push({
							name: ivdn.text,
							value: ivdn.sum - cnSum,
							color: treeColor,
						});
					}
				});
				object.value = object.children
					.map((item) => item.value)
					.reduce((a, b) => a + b, 0);
				this.result.push(object);
			});
		} else {
			// Build CN lookup by value[0] for outer dimension
			const outerCnMap = new Map<string, any>();
			for (const dd of tradeCNDD) {
				outerCnMap.set(dd.value[0] as string, dd);
			}

			tradeDD.forEach((trade) => {
				const subDD = trade.subDimension;
				const cnDD = includeCN
					? outerCnMap.get(trade.value[0] as string) : undefined;
				const cnSubDD = cnDD ? cnDD.subDimension : null;
				const cnSum = cnDD ? cnDD.sum : 0;

				// Build sub-dimension CN lookup
				const subCnMap = new Map<string, number>();
				if (cnSubDD) {
					for (const sub of cnSubDD) {
						subCnMap.set(sub.value[0] as string, sub.sum);
					}
				}

				this.result.push({
					name: trade.text,
					value: trade.sum - cnSum,
					color: treeColor,
					colorSaturation: [0.9, 0.4],
					children: subDD
						? subDD.map((subdd) => ({
							name: subdd.text,
							value: subdd.sum
								- (subCnMap.get(subdd.value[0] as string) || 0),
							color: treeColor,
							colorSaturation: [0.9, 0.4],
						}))
						: [],
				});
			});

			// Add AR Document group for stock groupings in AR mode
			if (!slMode && isStockGrouping && arDocumentAmount !== 0) {
				this.result.push({
					name: 'Customer Module Documents',
					value: arDocumentAmount,
					color: [
						'#f5a623', '#f8c471', '#fad7a0',
						'#fdebd0', '#fef9e7', '#fff5eb',
					],
					colorSaturation: [0.9, 0.4],
					children: [],
				});
			}
		}

		this.result = this.result.filter((item) => item.value > 0);

		this.series = [];

		const stock = {
			name: 'Sales Analysis',
			type: 'treemap',
			roam: 'move',
			width: '100%',
			drillDownIcon: '',
			label: {
				color: '#000',
			},
			itemStyle: {
				gapWidth: 1,
				borderWidth: 1,
			},
			levels: [
				{
					itemStyle: {
						borderWidth: 2,
						gapWidth: 2,
					},
					upperLabel: {
						show: false,
						fontWeight: 'bold',
						color: '#000',
					},
					emphasis: {
						itemStyle: {
							borderWidth: 0,
							gapWidth: 2,
						},
						upperLabel: {
							show: true,
							fontWeight: 'bold',
							color: '#000',
						},
					},
				},
				{
					colorSaturation: [0.3, 0.6],
					itemStyle: {
						borderColorSaturation: 0.7,
						gapWidth: 2,
						borderWidth: 2,
					},
					upperLabel: {
						show: true,
						color: '#000',
						fontWeight: 'bold',
					},
					emphasis: {
						itemStyle: {
							borderWidth: 0,
							gapWidth: 1,
						},
						upperLabel: {
							show: true,
							color: '#000',
							fontWeight: 'bold',
						},
					},
				},
				{
					itemStyle: {
						gapWidth: 1,
						borderWidth: 1,
					},
					upperLabel: {
						show: true,
						color: '#000',
						fontWeight: 'bold',
					},
					emphasis: {
						itemStyle: {
							borderWidth: 0,
							gapWidth: 1,
						},
						upperLabel: {
							show: true,
							color: '#000',
							fontWeight: 'bold',
						},
					},
				},
			],
			data: this.result,
		};

		this.series = [stock];

		this.saveHistory('result', 'series');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
